/**
 * AgencyProfile.jsx — Page publique du profil d'une agence
 *
 * Route : /agency/:agencyName
 * Affiche les informations de l'agence (nom, ville, compteur d'annonces)
 * et la liste de tous ses véhicules disponibles.
 *
 * Le nom de l'agence vient de l'URL (:agencyName encodé avec encodeURIComponent).
 * Les données sont récupérées via /api/products?agencyName=... et filtrées
 * pour n'afficher que les annonces avec isAvailable !== false.
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FiMapPin, FiCheckCircle, FiArrowLeft, FiMessageSquare, FiUsers } from 'react-icons/fi';
import Navbar from '../shared/Navbar.jsx';
import BottomNav from '../shared/BottomNav.jsx';
import { getApiBaseUrl } from '../../utils/apiBaseUrl';
import './AgencyProfile.css';

const apiBaseUrl = getApiBaseUrl();

function AgencyProfile() {
  const { agencyName } = useParams();
  const navigate = useNavigate();
  const decodedName = decodeURIComponent(agencyName || '');

  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState('');

  useEffect(() => {
    if (!decodedName) return;
    setLoading(true);
    fetch(`${apiBaseUrl}/api/products?agencyName=${encodeURIComponent(decodedName)}`)
      .then((r) => r.ok ? r.json() : Promise.reject())
      .then((data) => {
        setVehicles(Array.isArray(data) ? data.filter((v) => v.isAvailable !== false) : []);
      })
      .catch(() => setErreur('Impossible de charger les véhicules de cette agence.'))
      .finally(() => setLoading(false));
  }, [decodedName]);

  // Initiales de l'agence pour l'avatar (ex: "AutoMaroc Plus" → "AP")
  const initiales = decodedName.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();
  // Ville déduite du premier véhicule (toutes les annonces d'une agence partagent la même ville)
  const ville = vehicles[0]?.city || '';
  const totalAnnonces = vehicles.length;

  // Génère une miniature SVG inline quand une annonce n'a pas de photo
  const buildPlaceholder = (name) => {
    const ini = (name || 'V').slice(0, 2).toUpperCase();
    return `data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' width='400' height='240'><rect width='400' height='240' fill='%231a1a1a'/><text x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='sans-serif' font-size='48' fill='%23333'>${ini}</text></svg>`;
  };

  return (
    <div className="ap-page">
      <Navbar />

      <main className="ap-main">
        <div className="vx-shell">

          {/* ── Retour ── */}
          <button type="button" className="ap-back" onClick={() => navigate(-1)}>
            <FiArrowLeft size={14} /> Retour
          </button>

          {/* ── Header agence ── */}
          <header className="ap-header">
            <div className="ap-logo">{initiales}</div>
            <div className="ap-header-info">
              <h1 className="ap-name">{decodedName}</h1>
              <div className="ap-meta">
                {ville && <span><FiMapPin size={13} /> {ville}, Maroc</span>}
                <span><FiCheckCircle size={13} /> {totalAnnonces} annonce{totalAnnonces !== 1 ? 's' : ''}</span>
                <span><FiUsers size={13} /> Agence partenaire</span>
              </div>
            </div>
          </header>

          {/* ── Section véhicules ── */}
          <section className="ap-section">
            <div className="ap-section-head">
              <h2>Véhicules disponibles</h2>
              {!loading && !erreur && (
                <span className="ap-count">{totalAnnonces} résultat{totalAnnonces !== 1 ? 's' : ''}</span>
              )}
            </div>

            {loading && (
              <div className="ap-loading">Chargement des véhicules…</div>
            )}

            {erreur && (
              <div className="ap-error">{erreur}</div>
            )}

            {!loading && !erreur && vehicles.length === 0 && (
              <div className="ap-empty">Aucun véhicule disponible pour le moment.</div>
            )}

            {!loading && !erreur && vehicles.length > 0 && (
              <div className="ap-grid">
                {vehicles.map((v) => {
                  const nom = v.name || [v.vehicleBrand, v.vehicleModel].filter(Boolean).join(' ') || 'Véhicule';
                  const image = v.mainImageUrl || (v.galleryImages || [])[0] || buildPlaceholder(nom);
                  const note = Number(v.note || 0);
                  return (
                    <article key={v._id} className="ap-card">
                      <div className="ap-card-img">
                        <img src={image} alt={nom} loading="lazy" />
                        {note >= 4.5 && <span className="ap-badge ap-badge-top">Top noté</span>}
                        {note >= 4 && note < 4.5 && <span className="ap-badge ap-badge-good">Très bien</span>}
                      </div>
                      <div className="ap-card-body">
                        <span className="ap-card-agency">{v.agencyName || decodedName} — {v.city || ''}</span>
                        <span className="ap-card-name">{nom}</span>
                        <div className="ap-card-tags">
                          {v.seats && <span className="ap-tag">{v.seats} pl.</span>}
                          {v.transmission && <span className="ap-tag">{v.transmission}</span>}
                          {v.fuelType && <span className="ap-tag">{v.fuelType}</span>}
                          {v.vehicleYear && <span className="ap-tag">{v.vehicleYear}</span>}
                        </div>
                        <div className="ap-card-footer">
                          <span className="ap-card-price">
                            {Number(v.price || 0).toLocaleString('fr-FR')} <sub>MAD / jr</sub>
                          </span>
                          <Link to={`/vehicle/${v._id}`} className="ap-card-link">
                            Voir <span aria-hidden="true">→</span>
                          </Link>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </section>

          {/* ── CTA contact ── */}
          {!loading && vehicles.length > 0 && (
            <div className="ap-cta">
              <FiMessageSquare size={18} aria-hidden="true" />
              <p>Intéressé par un véhicule de {decodedName} ? Contactez l'agence directement depuis la fiche véhicule.</p>
            </div>
          )}

        </div>
      </main>

      <BottomNav />
    </div>
  );
}

export default AgencyProfile;
